












































const namespace = 'calendar';
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import EventBus from '@/communication/EventBus';
import CalendarDropdownFilters from '@/modules/calendar/components/CalendarDropdownFilters.vue';
import CalendarPills from '@/modules/calendar/components/CalendarPills.vue';
import CalendarContent from '@/modules/calendar/components/CalendarContent.vue';
import OdataResource from '@/modules/api/OdataResource';
import ICalendarData from '@/modules/calendar/interfaces/ICalendarData';
import { IFilters, IFilter } from '@/modules/calendar/interfaces/IFilter';
import { IDanceStyleCategory } from '@/modules/calendar/interfaces/IDanceStyleCategory';
import { CalendarFilter, PropertyFilter } from '@/modules/calendar/store/types';
import { AppTheme } from '@/plugins/vuetify/themes/types';
import { saveInLocalStorage, tryGetFromLocalStorage } from '@/utils/cache';
import { buildQuery } from '@/utils/api';
import { decodeUri } from '@/utils/hash';
import { getHourOnly, getMinutesOnly } from '@/utils/time';

@Component({
    name: 'Calendar',
    components: {
        CalendarDropdownFilters,
        CalendarPills,
        CalendarContent,
    },
})
export default class Calendar extends Vue {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Action('setWidths', { namespace }) private setWidths: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Action('setEvents', { namespace }) private setEvents: any;
    @Getter('getAllEvents', { namespace }) private getAllEvents!: ICalendarData[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Action('setFilteredEvents', { namespace }) private setFilteredEvents: any;
    @Getter('getFilteredEvents', { namespace }) private getFilteredEvents!: ICalendarData[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Action('setCalendarFilter', { namespace }) private setCalendarFilter: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Action('setHiddenFilters', { namespace }) private setHiddenFilters: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Action('cleanCalendarFilters', { namespace }) private cleanCalendarFilters: any;
    @Getter('getCalendarFilters', { namespace }) private getCalendarFilters!: { [key: string]: CalendarFilter };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Action('setHours', { namespace }) private setHours: any;
    @Getter('boxWidth', { namespace }) private boxWidth!: number;
    @Getter('boxType', { namespace }) private boxType!: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Action('setIsMobile', { namespace }) private setIsMobile: any;
    @Getter('isMobile', { namespace }) private isMobile!: number;
    @Getter('apiUrl', { namespace: 'configuration' }) private apiUrl!: string;
    @Getter('appTheme', { namespace: 'configuration' }) private appTheme!: AppTheme;

    private loading = false;
    private events: ICalendarData[] = [];
    private filters: IFilters = {};
    private selectedFilters: IFilters = {};
    private windowInnerWidth = window.innerWidth;

    private periodicalGroupsResource!: OdataResource<ICalendarData>;

    private get mobileView() {
        return this.isMobile;
    }

    sum = function(arr: number[]) {
        return arr.reduce((sum, x) => sum + x);
    };

    public mounted() {
        this.$nextTick(() => {
            window.addEventListener('resize', this.onResize);
        });

        const url = buildQuery(this.filters, this.apiUrl);
        this.periodicalGroupsResource = new OdataResource<ICalendarData>(
            url,
            this.onResourceLoading,
            this.onResourceLoadingFinished,
        );

        const userFilter = this.$route.query.userFilter;
        if (typeof userFilter === 'string') {
            const decodedUri: string = decodeUri(userFilter);
            this.filters = JSON.parse(decodedUri);
        }
        this.updateEvents();
        EventBus.$on('filtersChanged', this.applyCurrentFilters);
        EventBus.$on('themeChanged', this.applyTheme);
        EventBus.$on('environmentChanged', this.updateEvents);

        this.setDefaultFilters();
    }

    created() {
        window.addEventListener('resize', this.onResize, false);
        window.addEventListener('orientationchange', this.onResize, false);
        this.onResize();
    }

    destroyed() {
        window.removeEventListener('resize', this.onResize);
    }

    onResize() {
        const windowWidth = window.innerWidth > 0 ? window.innerWidth : screen.width;
        const width = document.getElementById('app')?.offsetWidth ?? windowWidth;
        this.windowInnerWidth = width;
        this.setIsMobile(this.windowInnerWidth < 768);
        this.updateWidths();
    }

    private onResourceLoading() {
        this.loading = true;
    }

    private onResourceLoadingFinished() {
        this.loading = false;
    }

    private get weekdaysContainerWidth() {
        const windowWidth = window.innerWidth > 0 ? window.innerWidth : screen.width;
        const width = document.getElementById('app')?.offsetWidth ?? windowWidth;
        return this.isMobile ? windowWidth : width - 97;
    }

    private applyTheme() {
        this.$vuetify.theme.themes.light = this.appTheme.theme.themes.light;
        this.$vuetify.theme.themes.dark = this.appTheme.theme.themes.dark;
    }

    private getWeekdayContainerWidth(events: ICalendarData[]): number {
        const numberOfWeekdays = this._.chain(events)
            .groupBy('WeekDayId')
            .map(value => ({ events: value.length }))
            .value().length;
        const weekdaysContainerWidth = this.weekdaysContainerWidth;
        const weekdayContainerWidth = this.isMobile
            ? weekdaysContainerWidth
            : weekdaysContainerWidth / numberOfWeekdays;

        return Math.floor(weekdayContainerWidth);
    }

    private getLocationWidth(events: ICalendarData[]): number {
        const maxNumberOfLocationsInAnyWeekDay = this._.chain(events)
            .groupBy('Address')
            .map(value => ({ events: value.length }))
            .value().length;

        const weekdayContainerWidth = this.getWeekdayContainerWidth(events);
        const locationWidth = this.isMobile
            ? weekdayContainerWidth
            : weekdayContainerWidth / maxNumberOfLocationsInAnyWeekDay;

        return Math.floor(locationWidth);
    }

    private getBoxWidth(events: ICalendarData[], weekdayContainerWidth: number, locationWidth: number): number {
        const eventsNumbers = this._.chain(events)
            .groupBy('Address')
            .map(value => ({
                eventsNumber: Math.max(
                    ...this._.chain(value)
                        .groupBy('WeekDayId')
                        .map(value => ({
                            weekdays: Math.max(
                                ...this._.chain(value)
                                    .groupBy('StartTime')
                                    .map(value => ({ times: value.length }))
                                    .value()
                                    .map(e => e.times),
                            ),
                        }))
                        .value()
                        .map(e => e.weekdays),
                ),
            }))
            .value()
            .map(e => e.eventsNumber);
        // const maxNumberOfEventsInAnyLocationInSingleHour = Math.max(...eventsNumbers);
        // TODO: find max number of events in location
        const maxNumberOfEventsInAnyLocationInSingleHour = 4;

        const boxWidth = this.isMobile
            ? weekdayContainerWidth / maxNumberOfEventsInAnyLocationInSingleHour - 3
            : locationWidth / maxNumberOfEventsInAnyLocationInSingleHour - 3;

        return Math.floor(boxWidth);
    }

    private getBoxType(boxWidth: number): string {
        if (this.isEnoughWidthToDisplayBigBox(boxWidth)) {
            return 'big';
        }

        if (this.isEnoughWidthToDisplayNormalBox(boxWidth) || this.isMobile) {
            return 'normal';
        }

        return 'small';
    }

    private isEnoughWidthToDisplayBigBox(boxWidth: number) {
        return boxWidth >= 450;
    }

    private isEnoughWidthToDisplayNormalBox(boxWidth: number) {
        return boxWidth >= 147;
    }

    private updateWidths() {
        const weekdayContainerWidth = this.getWeekdayContainerWidth(this.events);
        const locationWidth = this.getLocationWidth(this.events);
        const boxWidth = this.getBoxWidth(this.events, weekdayContainerWidth, locationWidth);
        const boxType = this.getBoxType(boxWidth);
        const cellHeight = this.isMobile ? 190 : boxType === 'big' ? 100 : boxType === 'normal' ? 64 : boxWidth / 2;
        this.setWidths({
            weekdaysContainerWidth: this.weekdaysContainerWidth,
            weekdayContainerWidth: weekdayContainerWidth,
            locationWidth: locationWidth,
            boxWidth: boxWidth,
            cellHeight: cellHeight,
            boxType,
        });
    }

    private updateHoursRange() {
        const events = this.getFilteredEvents;
        const eventsHours = events.map((event: ICalendarData) => this.getEventHours(event)).flat();
        const hours = [...new Set(eventsHours)];
        const allHours = this._.range(Math.min(...hours) - 1, Math.max(...hours) + 3, 0.5);
        const hoursWithEvents = [...new Set(hours)].concat([...new Set(hours)].map(n => n + 0.5)).sort((a, b) => a - b);
        this.setHours({ allHours, hoursWithEvents: hoursWithEvents });
    }

    private getEventHours(event: ICalendarData): number[] {
        const startHour = getHourOnly(event.StartTime);
        const endHour = getHourOnly(event.EndTime) + getMinutesOnly(event.EndTime) / 60;
        const eventHours = this._.range(startHour, endHour, 1);

        return eventHours;
    }

    private hourToNumber(houtString: string) {
        const hour =
            (new Date(`0001-01-01 ${houtString}`).getTime() - new Date(`0001-01-01 0:00`).getTime()) / (360 * 10000);
        return hour;
    }

    public async updateEvents() {
        const key = 'allCachedEvents:' + this.periodicalGroupsResource.baseUrl;
        this.events = tryGetFromLocalStorage(key);
        const events = this.events;
        this.selectedFilters = this.filters;
        this.setEvents(events);
        this.setFilteredEvents(events);
        this.applyCurrentFilters();

        const response = await this.periodicalGroupsResource.getResources('');
        if (response) {
            const events: ICalendarData[] = response;
            this.events = events;
            this.selectedFilters = this.filters;
            saveInLocalStorage(key, events);
            this.setEvents(events);
            this.setFilteredEvents(events);

            this.applyCurrentFilters();
        }

        this.updateHoursRange();
        this.updateWidths();
    }

    private applyFilter(
        events: ICalendarData[],
        propertyName: string,
        operator: string,
        value: string | number,
    ): ICalendarData[] {
        return events.filter((event: ICalendarData) => {
            if (Array.isArray(event[propertyName]) && propertyName === 'DanceStyleCategories') {
                const danceStyleCategories: IDanceStyleCategory[] = event[propertyName];
                const danceStyleCategoriesNames = danceStyleCategories.map(dsc => dsc.Name);
                return danceStyleCategoriesNames.includes(`${value}`);
            }

            switch (operator) {
                case 'eq': {
                    return event[propertyName] == value;
                }
                case 'lt': {
                    return event[propertyName] < value;
                }
                case 'gt': {
                    return event[propertyName] > value;
                }
                default: {
                    return event[propertyName] === value;
                }
            }
        });
    }

    private filter(filteredEvents: ICalendarData[], filter: CalendarFilter): ICalendarData[] {
        let result: ICalendarData[] = [];
        filter.propertyFilters.forEach((element: PropertyFilter) => {
            result = result.concat(
                this.applyFilter(filteredEvents, filter.propertyName, element.operator, element.value),
            );
        });
        return [...new Set(result)];
    }

    private applyCurrentFilters() {
        const calendarFilters: { [key: string]: CalendarFilter } = this.getCalendarFilters;

        // apply Filter
        let filteredEvents: ICalendarData[] = this.getAllEvents;
        const totalNumberOfFilters =
            Object.keys(calendarFilters).length === 0
                ? 0
                : this.sum([...Object.values(calendarFilters).map(filter => filter.propertyFilters.length)]);
        if (totalNumberOfFilters === 0) {
            filteredEvents = this.getAllEvents;
        } else {
            Object.keys(calendarFilters).forEach(key => {
                const filter = calendarFilters[key];
                if (filter.propertyFilters.length > 0) {
                    filteredEvents = this.filter(filteredEvents, filter);
                }
            });
        }

        // save Events
        this.setFilteredEvents(filteredEvents);
        this.events = this.getFilteredEvents;

        this.updateHoursRange();
        this.updateWidths();
    }

    private handleUpdateFilters(filters: IFilters): void {
        // set Calendar Filters
        const propertyName = filters.odataName;
        const propertyFilters = filters.details.map((x: IFilter) => ({
            operator: x.details.compareType,
            value: x.details.value,
        }));
        const calendarFilter = { propertyName: propertyName, propertyFilters: propertyFilters };

        this.setCalendarFilter(calendarFilter);
        this.applyCurrentFilters();
    }

    private setDefaultFilters() {
        const defaultFilters = this.getDefaultFilters();
        if (defaultFilters !== undefined) {
            this.cleanCalendarFilters();
            defaultFilters.forEach((filter: CalendarFilter) => {
                this.setCalendarFilter(filter);
            });
            this.applyCurrentFilters();
            EventBus.$emit('filtersChanged');
        }

        const hiddenFilters = this.getHiddenFilters();
        if (hiddenFilters !== undefined) {
            this.setHiddenFilters(hiddenFilters);
        }
    }

    private getDefaultFilters() {
        let defultFiltersString = document.getElementById('defaultFilters')?.innerText;
        if (defultFiltersString !== undefined) {
            defultFiltersString = decodeURIComponent(defultFiltersString);
            defultFiltersString = defultFiltersString.replace('new Date().toISOString()', new Date().toISOString());
            const defultFilters = JSON.parse(defultFiltersString);
            return defultFilters;
        }

        return undefined;
    }

    private getHiddenFilters() {
        const hiddenFiltersString = document.getElementById('hiddenFilters')?.innerText;
        if (hiddenFiltersString !== undefined) {
            return hiddenFiltersString;
        }

        return undefined;
    }
}
